import React, { useEffect, useRef, useState } from 'react'
import { Input, Button, FormItem, FormContainer, Alert } from 'components/ui'
import { ActionLink } from 'components/shared'
import { apiForgotPassword, apiResendOtp } from 'services/AuthService'
import ReCAPTCHA from 'react-google-recaptcha'
import useTimeOutMessage from 'utils/hooks/useTimeOutMessage'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import {
    setAuthPanelLoginExpand,
    setAuthPanelResetPasswordExpand,
    setAuthPanelSignupExpand,
} from 'store/theme/themeSlice'
import { useDispatch } from 'react-redux'
import ResetPasswordForm from '../ResetPassword/ResetPasswordForm'
import { useLocation } from 'react-router-dom'

const validationSchema = Yup.object().shape({
    email: Yup.string().required('Please enter your email'),
})

const ForgotPasswordForm = (props) => {
    const { disableSubmit = false, className } = props
    const location = useLocation()

    const dispatch = useDispatch()
    const recaptchaRef = useRef(null)

    const [showResetPassword, setShowResetPassword] = useState(false)

    const [emailSent, setEmailSent] = useState(false)

    const [message, setMessage] = useTimeOutMessage()
    const [emailAddress, setEmailAddress] = useState('')

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const withParam = searchParams.get('_with')
        const emailParam = searchParams.get('email')
        if (withParam === 'reset_pass' && emailParam) {
            setEmailSent(true)
            setEmailAddress(emailParam)
            setShowResetPassword(true)
        }
    }, [location])

    const onSendMail = async (values, setSubmitting) => {
        const recaptchaToken = await recaptchaRef.current.executeAsync()
        setSubmitting(true)
        try {
            const { email } = values
            const resp = await apiResendOtp({
                ...values,
                recaptchaToken,
                isForgotPasswordRequest: true,
            })
            if (resp.data) {
                setSubmitting(false)
                setEmailSent(true)
                setEmailAddress(email)
                setShowResetPassword(true)
            }
        } catch (errors) {
            setMessage(errors?.response?.data?.message || errors.toString())
            setSubmitting(false)
        }
    }

    return showResetPassword ? (
        <>
            <div className="mb-6 mt-3">
                <p>
                    Please enter the One-Time Password (OTP) sent to your
                    registered email address in the form below to proceed. If
                    you did not receive the OTP, you can request a new one using
                    the 'Resend OTP' option.
                </p>
            </div>
            <ResetPasswordForm email={emailAddress} />
        </>
    ) : (
        <div className={className}>
            <div className="mb-6">
                {emailSent ? (
                    <>
                        <h3 className="mb-1">Check your email</h3>
                        <p>
                            We have sent a password recovery instruction to your
                            email
                        </p>
                    </>
                ) : (
                    <>
                        <h3 className="mb-1 mt-3">Forgot Password</h3>
                        <p>
                            Please enter your email address to receive a
                            verification code
                        </p>
                    </>
                )}
            </div>
            {message && (
                <Alert className="mb-4" type="danger" showIcon>
                    {message}
                </Alert>
            )}
            <Formik
                initialValues={{
                    email: '',
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => {
                    if (!disableSubmit) {
                        onSendMail(values, setSubmitting)
                    } else {
                        setSubmitting(false)
                    }
                }}
            >
                {({ touched, errors, isSubmitting }) => (
                    <Form>
                        <FormContainer>
                            <div className={emailSent ? 'hidden' : ''}>
                                <FormItem
                                    invalid={errors.email && touched.email}
                                    errorMessage={errors.email}
                                >
                                    <Field
                                        type="email"
                                        autoComplete="off"
                                        name="email"
                                        placeholder="Email"
                                        component={Input}
                                    />
                                </FormItem>
                            </div>
                            <div className="hidden">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    size="invisible"
                                    sitekey="6LfHNskUAAAAALXWeqW6kUjlmcU_r6zHHTHXLRay"
                                />
                            </div>
                            <Button
                                block
                                loading={isSubmitting}
                                variant="solid"
                                type="submit"
                            >
                                {emailSent ? 'Resend Email' : 'Send Email'}
                            </Button>
                            <div className="mt-4 text-center">
                                <span>Back to </span>
                                <span
                                    className="text-primary cursor-pointer"
                                    onClick={() => {
                                        dispatch(
                                            setAuthPanelSignupExpand(false)
                                        )
                                        dispatch(
                                            setAuthPanelResetPasswordExpand(
                                                false
                                            )
                                        )
                                        dispatch(setAuthPanelLoginExpand(true))
                                    }}
                                >
                                    Sign In
                                </span>
                            </div>
                        </FormContainer>
                    </Form>
                )}
            </Formik>
        </div>
    )
}

export default ForgotPasswordForm
